import React from "react";
import { Box, CircularProgress, Grid2 as Grid, Paper, Typography, useTheme } from "@mui/material";
import { ButtonEx } from "@/lib/components/buttons/ButtonEx";
import { useTranslation } from "react-i18next";
import { OrderI18N, RequestI18N } from "@/translations";
import { RequestActions, RequestDTO, RequestStatus } from "@/api/data-contracts";
import { RequestStatusBadge } from "@/common/RequestStatusBadge";
import UndoIcon from "@mui/icons-material/Undo";
import CancelIcon from "@mui/icons-material/Cancel";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { KalenderIcon } from "@/agentur/AgenturIcons";
import { type RequestDialogHandlers } from "@/common/request-details/useRequestDialogs";

interface RequestBannerProps {
    model: RequestDTO;
    isOperator: boolean;
    readonly: boolean;
    processing: boolean;
    canExtendDates: boolean;
    hasQuotes: boolean;
    handlers: RequestDialogHandlers;
    onSave: () => void;
    onPublish: () => void;
}

/**
 * Banner displayed on the "Anfrage" tab when tabs are visible.
 *
 * Left side: "Anfrage" label, request number (boxhubNumber), RequestStatusBadge.
 * Right side: Status-dependent action buttons (save, publish, withdraw, cancel, delete, extend dates, quote matrix).
 */
const RequestBanner: React.FC<RequestBannerProps> = ({
    model,
    isOperator,
    readonly,
    processing,
    canExtendDates,
    hasQuotes,
    handlers,
    onSave,
    onPublish
}) => {
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <Paper elevation={3} sx={{ p: 2, mb: 2, width: "100%" }}>
            <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                <Grid>
                    <Typography variant="subtitle2" color="text.secondary">
                        {t(OrderI18N.tabs.tab.request)}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                        {model.boxhubNumber}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                        <RequestStatusBadge status={model.status} />
                    </Box>
                </Grid>

                <Grid>
                    <Grid container spacing={1}>
                        <ButtonEx
                            visible={hasQuotes}
                            startIcon={<KalenderIcon mode={theme.palette.mode} />}
                            onClick={handlers.handleOpenMatrix}
                            label={t(RequestI18N.details.action.quoteOverview)}
                        />
                        {canExtendDates && (
                            <ButtonEx
                                data-cy="extend-dates-button"
                                label={t(RequestI18N.details.action.extendDates)}
                                color="primary"
                                startIcon={<EventRepeatIcon />}
                                onClick={handlers.handleStartExtendDates}
                            />
                        )}
                        {isOperator &&
                            [
                                RequestStatus.PUBLISHED,
                                RequestStatus.TO_BE_CONFIRMED,
                                RequestStatus.IN_NEGOTIATION
                            ].includes(model.status) && (
                                <ButtonEx
                                    data-cy="resend-publish-notification-button"
                                    label={t(RequestI18N.details.action.resendPublishNotification)}
                                    color="primary"
                                    startIcon={<ForwardToInboxIcon />}
                                    onClick={handlers.handleResendPublishNotification}
                                />
                            )}
                        {isOperator && model.allowedActions?.includes(RequestActions.CONFIRM_REQUEST_INTERNALLY) && (
                            <ButtonEx
                                data-cy="confirm-internally-button"
                                label={t(RequestI18N.details.action.confirmInternally)}
                                color="success"
                                startIcon={<CheckCircleIcon />}
                                onClick={handlers.handleConfirmInternally}
                            />
                        )}
                        {!isOperator && model?.allowedActions?.includes(RequestActions.UNPUBLISH_REQUEST) && (
                            <ButtonEx
                                data-cy="unpublish-button"
                                label={t(RequestI18N.details.action.withdraw)}
                                color="warning"
                                startIcon={<UndoIcon />}
                                onClick={handlers.handleUnpublishRequest}
                            />
                        )}
                        {!isOperator &&
                            (model?.allowedActions?.includes(RequestActions.CANCEL_REQUEST) ||
                                model?.allowedActions?.includes(
                                    RequestActions.CANCEL_PREVIOUSLY_PUBLISHED_REQUEST
                                )) && (
                                <ButtonEx
                                    data-cy="rescind-button"
                                    label={t(OrderI18N.details.action.cancel)}
                                    color="error"
                                    startIcon={<CancelIcon />}
                                    onClick={handlers.handleCancelRequest}
                                />
                            )}
                        {!isOperator &&
                            !readonly &&
                            model?.id != null &&
                            model?.allowedActions?.includes(RequestActions.DELETE_REQUEST) && (
                                <Grid>
                                    <ButtonEx
                                        data-cy="delete-button"
                                        label={
                                            processing ? (
                                                <CircularProgress size={24} />
                                            ) : (
                                                t(RequestI18N.details.action.delete)
                                            )
                                        }
                                        color="error"
                                        disabled={processing}
                                        onClick={handlers.handleDeleteRequest}
                                    />
                                </Grid>
                            )}
                        {!isOperator && !readonly && (
                            <Grid>
                                <ButtonEx
                                    data-cy="capture-button"
                                    label={
                                        processing ? (
                                            <CircularProgress size={24} />
                                        ) : (
                                            t(RequestI18N.details.action.unpublishSave)
                                        )
                                    }
                                    disabled={processing}
                                    color="primary"
                                    onClick={onSave}
                                />
                            </Grid>
                        )}
                        {!isOperator && !readonly && (
                            <Grid>
                                <ButtonEx
                                    data-cy="publish-button"
                                    label={
                                        processing ? (
                                            <CircularProgress size={24} />
                                        ) : (
                                            t(RequestI18N.details.action.publish)
                                        )
                                    }
                                    disabled={processing}
                                    color="success"
                                    onClick={onPublish}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default RequestBanner;
